// ============================================================================
// SETTINGS → PRINT LAYOUTS (screen print-layouts, seed/172; ADR-0062)
// ============================================================================
// Per entity: the document contact details printed in headers (the entity row and
// one row per business unit), the output profile of each printable document
// (layout, paper, logo, signature, texts, which optional fields print, labels) with
// a live PDF preview, and the email template used by mail drafts and Share.
// Mandatory fields of the entity's country pack show locked: they can be relabelled,
// never hidden — the API enforces the same rule.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { EnterpriseScreenShell, actionButtonFactory, useCan, useStandardConfirms } from 'components/enterprise-shell';
import { SyutSelect, type SelectOption } from 'components/ui';
import notificationService from 'services/notificationService';
import { printingService, referenceService, organisationService } from 'api/services';
import type {
  OutputProfileDto, SaveOutputProfileRequest, DocumentEmailTemplateDto, OrgContactDto, StateOptionDto,
} from 'api/types';
import { useAuthContext } from 'contexts/AuthContext';

type Tab = 'contacts' | 'layout' | 'email';

const DOCUMENT_TYPES: SelectOption[] = [{ value: 'SALES_INVOICE', label: 'Sales Invoice / Service Invoice' }];
const PAPER: SelectOption[] = [{ value: 'A4', label: 'A4' }, { value: 'A5', label: 'A5' }, { value: 'LETTER', label: 'Letter' }];

const errorText = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  const details: string[] = Array.isArray(data?.errors) ? data.errors.filter((e: string) => !e.startsWith('StackTrace')) : [];
  return [data?.message, ...details].filter(Boolean).join(' — ') || fallback;
};

const toRequest = (p: OutputProfileDto): SaveOutputProfileRequest => ({
  layoutCode: p.layoutCode, accentColour: p.accentColour ?? null, paperSize: p.paperSize,
  marginMm: p.marginMm, topOffsetMm: p.topOffsetMm, fontScale: p.fontScale,
  extraHeaderLines: p.extraHeaderLines ?? '', termsText: p.termsText ?? '', declarationText: p.declarationText ?? '',
  bankDetailsText: p.bankDetailsText ?? '', signatureCaption: p.signatureCaption ?? '', footerText: p.footerText ?? '',
  defaultCopies: [...p.defaultCopies], hidden: [...p.hidden], labels: { ...p.labels }, rowVersion: p.rowVersion,
});

const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.25rem' };
const sectionTitle: React.CSSProperties = { fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '0.5rem 0 0.25rem' };

interface PrintLayoutsScreenProps { moduleIcon?: string; tabId?: string; moduleName?: string }

const PrintLayoutsScreen: React.FC<PrintLayoutsScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('print-layouts');
  const { confirmReset, ConfirmDialogPortal } = useStandardConfirms();
  const { selectedEntity } = useAuthContext();
  const [tab, setTab] = useState<Tab>('contacts');
  const [documentType, setDocumentType] = useState('SALES_INVOICE');

  // --- contact details ---------------------------------------------------------
  const [contacts, setContacts] = useState<OrgContactDto[]>([]);
  const [contactKey, setContactKey] = useState<string>('');            // '' = the entity row
  const [contactForm, setContactForm] = useState<OrgContactDto | null>(null);
  const [countries, setCountries] = useState<SelectOption[]>([]);
  const [states, setStates] = useState<StateOptionDto[]>([]);

  // --- profile -------------------------------------------------------------------
  const [profile, setProfile] = useState<OutputProfileDto | null>(null);
  const [draft, setDraft] = useState<SaveOutputProfileRequest | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [entities, setEntities] = useState<SelectOption[]>([]);
  const [copyFrom, setCopyFrom] = useState('');

  // --- email ---------------------------------------------------------------------
  const [template, setTemplate] = useState<DocumentEmailTemplateDto | null>(null);
  const [busy, setBusy] = useState(false);

  const keyOf = (c: OrgContactDto) => c.businessUnitId ?? '';

  const loadContacts = useCallback(async () => {
    try {
      const rows = await printingService.getContacts();
      setContacts(rows);
      setContactForm(rows.find(r => keyOf(r) === contactKey) ?? rows[0] ?? null);
    } catch (err) {
      notificationService.toast.error(errorText(err, 'Could not load contact details.'));
    }
  }, [contactKey]);

  const loadImage = useCallback(async (kind: 'logo' | 'signature', has: boolean) => {
    const set = kind === 'logo' ? setLogoUrl : setSignatureUrl;
    if (!has) { set(null); return; }
    const blob = await printingService.getImage(documentType, kind).catch(() => null);
    set(blob ? URL.createObjectURL(blob) : null);
  }, [documentType]);

  const loadProfile = useCallback(async () => {
    try {
      const p = await printingService.getProfile(documentType);
      setProfile(p); setDraft(toRequest(p));
      loadImage('logo', p.hasLogo); loadImage('signature', p.hasSignature);
      setTemplate(await printingService.getEmailTemplate(documentType));
    } catch (err) {
      notificationService.toast.error(errorText(err, 'Could not load the print layout.'));
    }
  }, [documentType, loadImage]);

  useEffect(() => { loadContacts(); loadProfile(); }, [selectedEntity?.id, documentType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    referenceService.getCountries().then(cs => setCountries(cs.map(c => ({ value: c.id, label: c.countryName })))).catch(() => setCountries([]));
    organisationService.get()
      .then(o => setEntities(o.entities.map(e => ({ value: e.id, label: `${e.code} — ${e.name}` }))))
      .catch(() => setEntities([]));
  }, []);

  useEffect(() => {
    const countryId = contactForm?.countryId;
    if (!countryId) { setStates([]); return; }
    printingService.getStates(countryId).then(setStates).catch(() => setStates([]));
  }, [contactForm?.countryId]);

  // ------------------------------------------------------------------ contacts

  const selectContact = (c: OrgContactDto) => { setContactKey(keyOf(c)); setContactForm({ ...c }); };
  const setContact = (patch: Partial<OrgContactDto>) => setContactForm(f => (f ? { ...f, ...patch } : f));

  const saveContact = async () => {
    if (!contactForm) return;
    setBusy(true);
    try {
      const saved = await printingService.saveContact({
        businessUnitId: contactForm.businessUnitId ?? null, printName: contactForm.printName, address: contactForm.address,
        city: contactForm.city, postalCode: contactForm.postalCode, stateProvinceId: contactForm.stateProvinceId || null,
        countryId: contactForm.countryId || null, phone: contactForm.phone, email: contactForm.email, website: contactForm.website,
        rowVersion: contactForm.rowVersion,
      });
      notificationService.toast.success(`Contact details of ${contactForm.ownerName} saved.`);
      setContactKey(keyOf(saved));
      await loadContacts();
    } catch (err: any) {
      notificationService.toast.error(errorText(err, 'Could not save the contact details.'));
      if (err?.response?.status === 409) loadContacts();
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------------------------------------------ profile

  const patch = (p: Partial<SaveOutputProfileRequest>) => setDraft(d => (d ? { ...d, ...p } : d));
  const toggleHidden = (code: string) => draft && patch({ hidden: draft.hidden.includes(code) ? draft.hidden.filter(h => h !== code) : [...draft.hidden, code] });
  const toggleCopy = (code: string) => draft && patch({ defaultCopies: draft.defaultCopies.includes(code) ? draft.defaultCopies.filter(c => c !== code) : [...draft.defaultCopies, code] });
  const setLabel = (code: string, value: string) => draft && patch({ labels: { ...draft.labels, [code]: value } });

  const saveProfile = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const p = await printingService.saveProfile(documentType, draft);
      setProfile(p); setDraft(toRequest(p));
      notificationService.toast.success('Print layout saved.');
    } catch (err: any) {
      notificationService.toast.error(errorText(err, 'Could not save the print layout.'));
      if (err?.response?.status === 409) loadProfile();
    } finally {
      setBusy(false);
    }
  };

  const resetProfile = async () => {
    if (!profile?.isSaved) return;
    const ok = await confirmReset({
      message: 'This removes the saved layout settings, logo and signature for this document. Invoices will print with the built-in defaults.',
    });
    if (!ok) return;
    try {
      const p = await printingService.resetProfile(documentType, profile.rowVersion);
      setProfile(p); setDraft(toRequest(p)); setLogoUrl(null); setSignatureUrl(null);
      notificationService.toast.success('Print layout reset to the defaults.');
    } catch (err) {
      notificationService.toast.error(errorText(err, 'Could not reset the print layout.'));
    }
  };

  const copyProfile = async () => {
    if (!copyFrom) return;
    try {
      const p = await printingService.copyProfile(documentType, copyFrom);
      setProfile(p); setDraft(toRequest(p)); setCopyFrom('');
      notificationService.toast.success('Settings copied. The logo and signature stay with their own entity.');
    } catch (err) {
      notificationService.toast.error(errorText(err, 'Could not copy the settings.'));
    }
  };

  const upload = async (kind: 'logo' | 'signature', file: File | undefined) => {
    if (!file) return;
    try {
      const p = await printingService.uploadImage(documentType, kind, file);
      setProfile(p); setDraft(d => (d ? { ...d, rowVersion: p.rowVersion } : d));
      loadImage(kind, true);
      notificationService.toast.success(kind === 'logo' ? 'Logo saved.' : 'Signature saved.');
    } catch (err) {
      notificationService.toast.error(errorText(err, 'Could not save the image.'));
    }
  };

  const removeImage = async (kind: 'logo' | 'signature') => {
    try {
      const p = await printingService.removeImage(documentType, kind);
      setProfile(p); setDraft(d => (d ? { ...d, rowVersion: p.rowVersion } : d));
      (kind === 'logo' ? setLogoUrl : setSignatureUrl)(null);
    } catch (err) {
      notificationService.toast.error(errorText(err, 'Could not remove the image.'));
    }
  };

  const preview = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const blob = await printingService.preview(documentType, draft);
      setPreviewUrl(old => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
    } catch (err: any) {
      let message = 'Could not render the preview.';
      const data = err?.response?.data;
      if (data instanceof Blob) { try { message = JSON.parse(await data.text())?.message || message; } catch { /* keep */ } }
      notificationService.toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------------------------------------------ email

  const saveTemplate = async () => {
    if (!template) return;
    setBusy(true);
    try {
      const t = await printingService.saveEmailTemplate(documentType, {
        subject: template.subject, body: template.body, cc: template.cc, bcc: template.bcc,
        fileNamePattern: template.fileNamePattern, rowVersion: template.rowVersion,
      });
      setTemplate(t);
      notificationService.toast.success('Email template saved.');
    } catch (err: any) {
      notificationService.toast.error(errorText(err, 'Could not save the email template.'));
      if (err?.response?.status === 409) loadProfile();
    } finally {
      setBusy(false);
    }
  };

  const stateOptions: SelectOption[] = useMemo(
    () => states.map(s => ({ value: s.id, label: s.taxStateCode ? `${s.name} (${s.taxStateCode})` : s.name })), [states]);
  const otherEntities = useMemo(() => entities.filter(e => e.value !== selectedEntity?.id), [entities, selectedEntity?.id]);
  const canEdit = can('UPDATE');

  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(() => { loadContacts(); loadProfile(); }),
  ];

  const tabButton = (id: Tab, label: string) => (
    <button key={id} className={`btn ${tab === id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(id)}
      style={{ padding: '0.35rem 0.9rem', fontSize: '0.8125rem' }}>{label}</button>
  );

  return (
    <EnterpriseScreenShell
      title="Print Layouts"
      description={`How documents print and email for ${selectedEntity?.name ?? 'this entity'}`}
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.print size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      ConfirmDialogPortal={ConfirmDialogPortal}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.75rem', height: '100%', overflow: 'auto' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {tabButton('contacts', 'Contact details')}
          {tabButton('layout', 'Layout & branding')}
          {tabButton('email', 'Email')}
          {tab !== 'contacts' && (
            <div style={{ marginLeft: 'auto', minWidth: 280 }}>
              <SyutSelect options={DOCUMENT_TYPES} value={documentType} isClearable={false} size="sm"
                onChange={v => setDocumentType((v as string) || 'SALES_INVOICE')} />
            </div>
          )}
        </div>

        {/* ---------------------------------------------------------------- contacts */}
        {tab === 'contacts' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) 1fr', gap: '1rem' }}>
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'hidden' }}>
              {contacts.map(c => (
                <button key={keyOf(c) || 'entity'} onClick={() => selectContact(c)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 0.75rem', border: 'none',
                    borderBottom: '1px solid var(--border-color)', cursor: 'pointer',
                    background: keyOf(c) === keyOf(contactForm ?? c) && contactForm ? 'var(--bg-secondary)' : 'transparent',
                    color: 'var(--text-primary)',
                  }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{c.ownerName}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                    {c.businessUnitId ? `Business unit ${c.ownerCode ?? ''}` : 'Entity'} · {c.id ? 'set' : 'not set'}
                  </div>
                </button>
              ))}
            </div>
            {contactForm && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem', alignContent: 'start' }}>
                <div style={{ gridColumn: '1 / -1', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  {contactForm.businessUnitId
                    ? 'Printed on documents of this business unit. Leave a field blank to use the entity\'s.'
                    : 'Printed on every document unless a business unit has its own.'}
                </div>
                <label style={fieldStyle}><span className="form-label">Print name</span>
                  <input className="form-input" value={contactForm.printName ?? ''} disabled={!canEdit}
                    placeholder={contactForm.businessUnitId ? 'Entity print name' : contactForm.ownerName}
                    onChange={e => setContact({ printName: e.target.value })} /></label>
                <label style={{ ...fieldStyle, gridColumn: '1 / -1' }}><span className="form-label">Address</span>
                  <textarea className="form-input" rows={3} value={contactForm.address ?? ''} disabled={!canEdit}
                    onChange={e => setContact({ address: e.target.value })} /></label>
                <label style={fieldStyle}><span className="form-label">City</span>
                  <input className="form-input" value={contactForm.city ?? ''} disabled={!canEdit} onChange={e => setContact({ city: e.target.value })} /></label>
                <label style={fieldStyle}><span className="form-label">Postal code</span>
                  <input className="form-input" value={contactForm.postalCode ?? ''} disabled={!canEdit} onChange={e => setContact({ postalCode: e.target.value })} /></label>
                <div style={fieldStyle}>
                  <SyutSelect label="Country" options={countries} value={contactForm.countryId ?? ''} isDisabled={!canEdit} size="sm"
                    onChange={v => setContact({ countryId: (v as string) || null, stateProvinceId: null })} />
                </div>
                <div style={fieldStyle}>
                  <SyutSelect label="State / province" options={stateOptions} value={contactForm.stateProvinceId ?? ''} isDisabled={!canEdit || !contactForm.countryId} size="sm"
                    onChange={v => setContact({ stateProvinceId: (v as string) || null })} />
                </div>
                <label style={fieldStyle}><span className="form-label">Phone</span>
                  <input className="form-input" value={contactForm.phone ?? ''} disabled={!canEdit} onChange={e => setContact({ phone: e.target.value })} /></label>
                <label style={fieldStyle}><span className="form-label">Email</span>
                  <input className="form-input" value={contactForm.email ?? ''} disabled={!canEdit} onChange={e => setContact({ email: e.target.value })} /></label>
                <label style={fieldStyle}><span className="form-label">Website</span>
                  <input className="form-input" value={contactForm.website ?? ''} disabled={!canEdit} onChange={e => setContact({ website: e.target.value })} /></label>
                {canEdit && (
                  <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={saveContact} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ---------------------------------------------------------------- layout */}
        {tab === 'layout' && profile && draft && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(360px, 1fr) minmax(360px, 1fr)', gap: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                Country rules: <strong>{profile.countryPackName}</strong>{profile.jurisdictionCode ? ` (${profile.jurisdictionCode})` : ''} ·{' '}
                {profile.isSaved ? `saved${profile.updatedByName ? ` by ${profile.updatedByName}` : ''}` : 'built-in defaults'}
              </div>

              <div style={sectionTitle}>Page</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                <SyutSelect label="Layout" options={profile.layouts.map(l => ({ value: l.code, label: l.name }))} value={draft.layoutCode}
                  isClearable={false} isDisabled={!canEdit} size="sm" onChange={v => patch({ layoutCode: (v as string) || 'STANDARD_A4' })} />
                <SyutSelect label="Paper" options={PAPER} value={draft.paperSize} isClearable={false} isDisabled={!canEdit} size="sm"
                  onChange={v => patch({ paperSize: (v as string) || 'A4' })} />
                <label style={fieldStyle}><span className="form-label">Accent colour</span>
                  <input type="color" value={draft.accentColour || '#1F4E79'} disabled={!canEdit}
                    onChange={e => patch({ accentColour: e.target.value.toUpperCase() })} style={{ height: 32, width: '100%' }} /></label>
                <label style={fieldStyle}><span className="form-label">Font size (%)</span>
                  <input type="number" className="form-input" min={80} max={130} step={5} disabled={!canEdit}
                    value={Math.round(draft.fontScale * 100)} onChange={e => patch({ fontScale: Number(e.target.value) / 100 })} /></label>
                <label style={fieldStyle}><span className="form-label">Margin (mm)</span>
                  <input type="number" className="form-input" min={5} max={40} disabled={!canEdit}
                    value={draft.marginMm} onChange={e => patch({ marginMm: Number(e.target.value) })} /></label>
                <label style={fieldStyle}><span className="form-label">Letterhead space (mm)</span>
                  <input type="number" className="form-input" min={0} max={100} disabled={!canEdit}
                    value={draft.topOffsetMm} onChange={e => patch({ topOffsetMm: Number(e.target.value) })} /></label>
              </div>

              <div style={sectionTitle}>Logo and signature</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {(['logo', 'signature'] as const).map(kind => {
                  const url = kind === 'logo' ? logoUrl : signatureUrl;
                  const has = kind === 'logo' ? profile.hasLogo : profile.hasSignature;
                  return (
                    <div key={kind} style={{ border: '1px dashed var(--border-color)', borderRadius: '0.5rem', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      <div className="form-label">{kind === 'logo' ? 'Logo' : 'Authorised signature'}</div>
                      <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)', borderRadius: '0.35rem' }}>
                        {url ? <img src={url} alt={kind} style={{ maxHeight: 56, maxWidth: '100%' }} /> : <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>None</span>}
                      </div>
                      {canEdit && (
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <label className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer' }}>
                            <IconSystem.upload size={14} /> Upload
                            <input type="file" accept="image/png,image/jpeg" hidden onChange={e => { upload(kind, e.target.files?.[0]); e.target.value = ''; }} />
                          </label>
                          {has && <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem' }} onClick={() => removeImage(kind)}>Remove</button>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>PNG or JPEG, up to 1 MB. Saved at once, separately from the other settings.</div>

              <div style={sectionTitle}>Texts</div>
              {([
                ['extraHeaderLines', 'Extra header lines (e.g. FSSAI, MSME no.) — one per line', 2],
                ['bankDetailsText', 'Bank details', 3],
                ['termsText', 'Terms & conditions', 3],
                ['declarationText', 'Declaration', 2],
                ['footerText', 'Footer', 1],
              ] as const).map(([key, label, rows]) => (
                <label key={key} style={fieldStyle}><span className="form-label">{label}</span>
                  <textarea className="form-input" rows={rows} disabled={!canEdit} value={(draft as any)[key] ?? ''}
                    onChange={e => patch({ [key]: e.target.value } as Partial<SaveOutputProfileRequest>)} /></label>
              ))}
              <label style={fieldStyle}><span className="form-label">Signature caption ({'{EntityName}'} is replaced)</span>
                <input className="form-input" disabled={!canEdit} placeholder="For {EntityName}" value={draft.signatureCaption ?? ''}
                  onChange={e => patch({ signatureCaption: e.target.value })} /></label>

              <div style={sectionTitle}>Copies ticked by default</div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {profile.copyOptions.map(c => (
                  <label key={c.code} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8125rem' }}>
                    <input type="checkbox" disabled={!canEdit} checked={draft.defaultCopies.includes(c.code)} onChange={() => toggleCopy(c.code)} />
                    {c.caption}
                  </label>
                ))}
              </div>

              <div style={sectionTitle}>Fields</div>
              <table className="table" style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                    <th style={{ padding: '0.3rem' }}>Print</th><th style={{ padding: '0.3rem' }}>Field</th><th style={{ padding: '0.3rem' }}>Label on the document</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.fields.map(f => (
                    <tr key={f.code} style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.3rem' }}>
                        {f.mandatory
                          ? <span title={f.mandatoryReason ?? 'Required'}><IconSystem.lock size={14} /></span>
                          : f.canHide
                            ? <input type="checkbox" disabled={!canEdit} checked={!draft.hidden.includes(f.code)} onChange={() => toggleHidden(f.code)} />
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ padding: '0.3rem' }}>
                        {f.name}
                        {f.mandatory && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Required: {f.mandatoryReason}</div>}
                      </td>
                      <td style={{ padding: '0.3rem' }}>
                        <input className="form-input" style={{ padding: '0.2rem 0.4rem', fontSize: '0.8125rem' }} disabled={!canEdit}
                          placeholder={f.defaultLabel} value={draft.labels[f.code] ?? ''} onChange={e => setLabel(f.code, e.target.value)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.5rem' }}>
                {canEdit && otherEntities.length > 0 && (
                  <>
                    <div style={{ minWidth: 220 }}>
                      <SyutSelect label="Copy settings from" options={otherEntities} value={copyFrom} size="sm"
                        onChange={v => setCopyFrom((v as string) || '')} />
                    </div>
                    <button className="btn btn-secondary" onClick={copyProfile} disabled={!copyFrom}>Copy</button>
                  </>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                  {can('DELETE') && profile.isSaved && <button className="btn btn-secondary" onClick={resetProfile}>Reset to defaults</button>}
                  <button className="btn btn-secondary" onClick={preview} disabled={busy}>Preview</button>
                  {canEdit && <button className="btn btn-primary" onClick={saveProfile} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>}
                </div>
              </div>
            </div>

            <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', minHeight: 600, display: 'flex', alignItems: 'stretch', justifyContent: 'center', background: 'var(--bg-secondary)' }}>
              {previewUrl
                ? <iframe title="Preview" src={previewUrl} style={{ width: '100%', minHeight: 600, border: 'none' }} />
                : <div style={{ alignSelf: 'center', fontSize: '0.8125rem', color: 'var(--text-muted)', padding: '1rem', textAlign: 'center' }}>
                    Click <strong>Preview</strong> to see a sample invoice with these settings — saved or not.
                  </div>}
            </div>
          </div>
        )}

        {/* ---------------------------------------------------------------- email */}
        {tab === 'email' && template && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxWidth: 760 }}>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              Used by <strong>Open in mail app</strong> and <strong>Share</strong> on the invoice. {template.isSaved ? '' : 'Showing the built-in default.'}
            </div>
            <label style={fieldStyle}><span className="form-label">Subject</span>
              <input className="form-input" disabled={!canEdit} value={template.subject} onChange={e => setTemplate({ ...template, subject: e.target.value })} /></label>
            <label style={fieldStyle}><span className="form-label">Body</span>
              <textarea className="form-input" rows={8} disabled={!canEdit} value={template.body} onChange={e => setTemplate({ ...template, body: e.target.value })} /></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <label style={fieldStyle}><span className="form-label">Default CC</span>
                <input className="form-input" disabled={!canEdit} value={template.cc ?? ''} onChange={e => setTemplate({ ...template, cc: e.target.value })} /></label>
              <label style={fieldStyle}><span className="form-label">BCC</span>
                <input className="form-input" disabled={!canEdit} value={template.bcc ?? ''} onChange={e => setTemplate({ ...template, bcc: e.target.value })} /></label>
            </div>
            <label style={fieldStyle}><span className="form-label">Attachment file name (without .pdf)</span>
              <input className="form-input" disabled={!canEdit} placeholder="{DocumentNo}" value={template.fileNamePattern ?? ''}
                onChange={e => setTemplate({ ...template, fileNamePattern: e.target.value })} /></label>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Placeholders: {template.tokens.join(' ')}</div>
            {canEdit && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={saveTemplate} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </EnterpriseScreenShell>
  );
};

export default PrintLayoutsScreen;
